import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { EquipmentWithTranslationKeys } from './standard-equipment';
import { StyledStandardEquipmentSectionWrapper } from './styles/standard-equipment-sections-style';
import { useI18n } from '../../i18n/i18n';
import { Layout, Headline, Text, Icon, Table, TableBody, TableRow, TableCell, } from '@oneaudi/unified-web-alpha-components';
import { useCarlineStore } from '../../store/carline';
import { RenderHtml } from '../../utils/render-html';
import { GetUrlArrayFromImageSet } from '../../utils/images';
import { useDebugMode } from '../../utils/debug-mode';
import { MoreInformationDialog } from '../more-information/more-information-dialog';
import { useTracking } from '../../hooks/use-tracking';
import { EventAction } from '../../types/tracking';
export const StandardEquipmentSections = ({ groupedStandardEquipment, secondGroupedStandardEquipment, }) => {
    const [openAccordions, setOpenAccordions] = useState(null);
    const mergedGroups = StandartEquipmentMerger({
        firstStandardEquipment: groupedStandardEquipment,
        secondStandardEquipment: secondGroupedStandardEquipment,
    });
    return (_jsx("div", { children: mergedGroups.map((groupData) => {
            var _a, _b;
            return (_jsx(StandardEquipmentSection, { groupId: groupData.id, group: groupData.name, firstCarGroupStandardEquipment: ((_a = groupedStandardEquipment.find((a) => a.group.id === groupData.id)) === null || _a === void 0 ? void 0 : _a.families) || [], secondCarGroupStandardEquipment: ((_b = secondGroupedStandardEquipment.find((a) => a.group.id === groupData.id)) === null || _b === void 0 ? void 0 : _b.families) || [], openAccordions: openAccordions, setOpenAccordions: setOpenAccordions }, groupData.id));
        }) }));
};
export const StandartEquipmentMerger = ({ firstStandardEquipment, secondStandardEquipment, }) => {
    return useMemo(() => {
        const merged = [];
        const extractGroups = (arr) => {
            if (!arr)
                return [];
            return arr.map((item) => {
                if ('group' in item) {
                    return { id: item.group.id, name: item.group.name };
                }
                return { id: item.id, name: item.name };
            });
        };
        const allGroups = [
            ...extractGroups(firstStandardEquipment),
            ...extractGroups(secondStandardEquipment),
        ];
        allGroups.forEach((group) => {
            if (!merged.find((g) => g.id === group.id)) {
                merged.push(group);
            }
        });
        return merged;
    }, [firstStandardEquipment, secondStandardEquipment]);
};
const StandardEquipmentRow = ({ feature, setDialogData, selectedEngine, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { isDebug } = useDebugMode();
    const { handleTrackEvent, baseTrackingEventName } = useTracking();
    if (!feature)
        return _jsx(Text, { variant: { type: 'copy', order: '1', style: 'normal' }, children: "-" });
    const featureName = feature.name;
    const introText = (_b = (_a = feature.texts) === null || _a === void 0 ? void 0 : _a.find((text) => text.key === 'intro')) === null || _b === void 0 ? void 0 : _b.value;
    const benefits = (_c = feature.benefits) === null || _c === void 0 ? void 0 : _c.filter(Boolean);
    const description = (_e = (_d = feature.texts) === null || _d === void 0 ? void 0 : _d.find((text) => text.key === 'ak_text')) === null || _e === void 0 ? void 0 : _e.value;
    const remarks = (_g = (_f = feature.texts) === null || _f === void 0 ? void 0 : _f.find((text) => text.key === 'ak_remarks')) === null || _g === void 0 ? void 0 : _g.value;
    const image = ((_h = GetUrlArrayFromImageSet(feature.mediaSets, 'small_hd_image')) === null || _h === void 0 ? void 0 : _h.filter((url) => Boolean(url))) || [];
    const isEmptyFeature = !introText && !(benefits === null || benefits === void 0 ? void 0 : benefits.length) && !description && !image.length && !remarks;
    const renderFeatureName = (_jsxs(Text, { variant: { type: 'copy', order: '1', style: 'normal' }, children: [_jsx(RenderHtml, { html: featureName }), ' ', isDebug && (_jsx(Text, { tag: "span", variant: { type: 'copy', order: '1', style: 'bold' }, children: ` [${feature.pr7}]` }))] }));
    const handleTrackOpenDataLayerEvent = () => {
        handleTrackEvent({
            eventInfo: {
                eventName: `${baseTrackingEventName} - click to open details layer`,
                eventAction: EventAction.Internal_Link,
            },
            attributes: {
                label: featureName || '',
                elementName: 'other',
                value: (selectedEngine === null || selectedEngine === void 0 ? void 0 : selectedEngine.engineName) || '',
            },
        });
    };
    return isEmptyFeature ? (_jsx("div", { className: "custom-item", children: renderFeatureName })) : (_jsxs("button", { onClick: () => {
            setDialogData({
                featureName: featureName || '',
                introText: introText || '',
                benefits: benefits || [],
                description: description || '',
                remarks: remarks || '',
                image: image || undefined,
                pr7: feature.pr7 || '',
                texts: feature.texts || [],
            });
            handleTrackOpenDataLayerEvent();
        }, className: "custom-item", children: [renderFeatureName, _jsx(Icon, { size: "s", name: "forward" })] }));
};
const StandardEquipmentSectionRow = ({ firstFamilyMatch, secondFamilyMatch, compare, setDialogData, }) => {
    const { selectedEngine, secondSelectedEngine } = useCarlineStore();
    const itemKeyList = ItemKeyList({
        firstFamily: firstFamilyMatch,
        secondFamily: secondFamilyMatch,
    });
    return itemKeyList.map((featureKey) => {
        var _a, _b;
        return (_jsxs(TableRow, { children: [_jsx(TableCell, { width: "50%", children: _jsx(StandardEquipmentRow, { feature: ((_a = firstFamilyMatch === null || firstFamilyMatch === void 0 ? void 0 : firstFamilyMatch.items) === null || _a === void 0 ? void 0 : _a.find((a) => { var _a; return (_a = a === null || a === void 0 ? void 0 : a.pr7) === null || _a === void 0 ? void 0 : _a.startsWith(featureKey); })) || null, setDialogData: setDialogData, selectedEngine: selectedEngine }) }), compare && (_jsx(TableCell, { width: "50%", children: _jsx(StandardEquipmentRow, { selectedEngine: secondSelectedEngine, feature: ((_b = secondFamilyMatch === null || secondFamilyMatch === void 0 ? void 0 : secondFamilyMatch.items) === null || _b === void 0 ? void 0 : _b.find((a) => { var _a; return (_a = a === null || a === void 0 ? void 0 : a.pr7) === null || _a === void 0 ? void 0 : _a.startsWith(featureKey); })) || null, setDialogData: setDialogData }) }))] }, featureKey));
    });
};
const ItemKeyList = ({ firstFamily, secondFamily, }) => {
    return useMemo(() => {
        const merged = [];
        const extractPr7Prefixes = (family) => {
            if (!family || !family.items)
                return [];
            return family.items
                .map((item) => {
                var _a;
                return ((_a = item === null || item === void 0 ? void 0 : item.pr7) === null || _a === void 0 ? void 0 : _a.substring(0, 4)) || '';
            })
                .filter(Boolean);
        };
        const allPrefixes = [...extractPr7Prefixes(firstFamily), ...extractPr7Prefixes(secondFamily)];
        allPrefixes.forEach((prefix) => {
            if (!merged.includes(prefix)) {
                merged.push(prefix);
            }
        });
        return merged;
    }, [firstFamily, secondFamily]);
};
const StandardEquipmentSection = ({ groupId, group, firstCarGroupStandardEquipment, secondCarGroupStandardEquipment, }) => {
    const { i18n } = useI18n();
    const { compare } = useCarlineStore();
    const [dialogData, setDialogData] = useState(null);
    if (firstCarGroupStandardEquipment.length === 0 &&
        secondCarGroupStandardEquipment.length === null)
        return null;
    const families = StandartEquipmentMerger({
        firstStandardEquipment: firstCarGroupStandardEquipment,
        secondStandardEquipment: secondCarGroupStandardEquipment,
    });
    const finalGroupName = EquipmentWithTranslationKeys.includes(group)
        ? i18n(`compareLayer.standardEquipment.sections.${group}`)
        : group;
    return (_jsxs("div", { children: [_jsxs(Layout, { direction: "column", rowGap: "20px", margin: [0, 0, '60px', 0], children: [_jsx(Headline, { tag: "span", variant: { type: 'headline', order: '3', weight: 'normal' }, children: finalGroupName }), families.map((family) => {
                        const firstFamilyMatch = firstCarGroupStandardEquipment.find((a) => a.id === family.id);
                        const secondFamilyMatch = secondCarGroupStandardEquipment.find((a) => a.id === family.id);
                        return (_jsxs(StyledStandardEquipmentSectionWrapper, { children: [_jsx(Headline, { tag: "span", variant: {
                                        order: '4',
                                        weight: 'normal',
                                        type: 'headline',
                                    }, children: family.name }), _jsx(Table, { displayMode: "table", children: _jsx(TableBody, { children: _jsx(StandardEquipmentSectionRow, { firstFamilyMatch: firstFamilyMatch, secondFamilyMatch: secondFamilyMatch, compare: compare, setDialogData: setDialogData }) }) })] }, `${family.id}-${groupId}`));
                    })] }), _jsx(MoreInformationDialog, { dialogData: dialogData, setDialogData: setDialogData })] }));
};
